/**
 * The cloud-save client: probe once, pull before the game reads its saves, then write through.
 * localStorage stays the store of record - this only notices what changed there and sends it, a
 * little later, to the server the page came from. With no such server `probe()` says so once and
 * every other method is a no-op: not a request, not a storage write, not a timer.
 */

import { adoptCode } from './adoptCode';
import { applyMerge, createLocalStore } from './localStore';
import { planMerge } from './merge';
import { MSG, refusedMessage } from './messages';
import { buildPushBody } from './pushBody';
import { newCode } from './saveCode';
import { fingerprint, freshState, loadState, saveState } from './state';
import { BOOT_PULL_TIMEOUT_MS, createTransport, KEEPALIVE_MAX_BYTES } from './transport';
import type { CloudDeps, CloudKey, CloudSave, CloudState, CloudStatus, SaveDocument } from './types';
import { CLOUD_KEY, CLOUD_KEYS, isCloudKey } from './types';

/** How long a change waits for company before it is sent. */
export const PUSH_DELAY_MS = 2000;
/** The suspend save is large and rewritten every 5 s during a mission: it goes less often. */
export const SUSPEND_PUSH_DELAY_MS = 6000;
export const RETRY_MIN_MS = 2000;
export const RETRY_MAX_MS = 60_000;

export function createCloudSave(deps: CloudDeps): CloudSave {
  const { storage } = deps;
  const local = createLocalStore(storage);
  const now = deps.now ?? (() => Date.now());
  const timers = deps.timers ?? {
    set: (run: () => void, ms: number): unknown => setTimeout(run, ms),
    clear: (handle: unknown): void => clearTimeout(handle as ReturnType<typeof setTimeout>),
  };
  const transport = createTransport(deps.fetch, deps.location.origin);
  const listeners = new Set<() => void>();
  /** Each key's value as this layer last saw it, so `touch` can tell a real change from a rewrite. */
  const known = new Map<CloudKey, string>();

  let probing: Promise<boolean> | null = null;
  let on = false;
  let state: CloudState | null = null;
  let timer: unknown = null;
  let timerAt = Infinity;
  let inFlight: Promise<void> | null = null;
  let again = false;
  let failures = 0;
  /** Set for the rest of the session: by another device's save, a refusal, or a code change awaiting the reload. */
  let parked: string | null = null;
  /** `useCode` replaced the saves: what the running game writes now is the OLD game, and is not sent anywhere. */
  let replaced = false;
  let note = '';
  let conflictNote = '';

  const persist = (): void => {
    if (storage !== null && state !== null) saveState(storage, state);
  };
  const changed = (): void => {
    for (const listener of [...listeners]) listener();
  };
  const remember = (): void => {
    for (const key of CLOUD_KEYS) known.set(key, local.read(key) ?? '');
  };
  const mergeWith = (s: CloudState, remote: SaveDocument): ReturnType<typeof planMerge> =>
    planMerge({ local: local.values(), dirty: new Set(s.dirty), base: s.base, remote, dirtyAt: s.dirtyAt });

  function status(): CloudStatus {
    const lastSyncedAt = state?.syncedAt ?? null;
    if (!on) return { state: 'off', message: '', lastSyncedAt: null };
    if (parked !== null) return { state: 'paused', message: parked, lastSyncedAt };
    if (inFlight !== null) return { state: 'syncing', message: conflictNote || note, lastSyncedAt };
    if (failures > 0) return { state: 'offline', message: MSG.offline, lastSyncedAt };
    if (conflictNote !== '') return { state: 'conflict-resolved', message: conflictNote, lastSyncedAt };
    return { state: 'idle', message: note, lastSyncedAt };
  }

  function cancelTimer(): void {
    if (timer !== null) timers.clear(timer);
    timer = null;
    timerAt = Infinity;
  }

  /** Runs from the FIRST unsent change and is only ever pulled earlier: a key rewritten every 5 s still goes. */
  function schedule(ms: number): void {
    const at = now() + ms;
    if (timer !== null && timerAt <= at) return;
    cancelTimer();
    timerAt = at;
    timer = timers.set(() => {
      timer = null;
      timerAt = Infinity;
      void push(false);
    }, ms);
  }

  function park(why: string): void {
    parked = why;
    cancelTimer();
  }

  async function pushOnce(keepalive: boolean, mayRetry: boolean): Promise<void> {
    const s = state;
    if (!on || s === null || parked !== null || s.dirty.length === 0) return;
    // The first upload carries everything this device holds, not only what changed since the page opened.
    const all = s.revision === 0;
    const keys = all ? CLOUD_KEYS.filter((k) => s.dirty.includes(k) || local.read(k) !== null) : [...s.dirty];
    const built = buildPushBody(s.revision, keys, local.read);
    if (built.oversize.length > 0) {
      s.dirty = s.dirty.filter((k) => !built.oversize.includes(k));
      note = MSG.tooLarge;
      persist();
    }
    if (built.body === null) return;
    const result = await transport.put(s.code, built.body, keepalive && built.bytes <= KEEPALIVE_MAX_BYTES);
    // A code change swapped the state while this was in the air: the answer is about a save no longer in use.
    if (state !== s) return;
    if (result.kind === 'ok') {
      for (const [key, value] of built.sent) {
        s.base[key] = fingerprint(value);
        // Written again while the request was in flight: the newer value still has to go.
        if ((local.read(key) ?? '') === value) s.dirty = s.dirty.filter((k) => k !== key);
      }
      s.revision = result.revision;
      s.syncedAt = now();
      if (s.dirty.length === 0) s.dirtyAt = null;
      failures = 0;
      // "Nothing has been sent yet" stopped being true.
      if (note !== MSG.tooLarge) note = '';
      persist();
    } else if (result.kind === 'conflict') {
      const plan = mergeWith(s, result.doc);
      // The running game holds its profile in memory, so the server's campaign cannot be loaded
      // under it - and sending ours would destroy theirs unseen. Stop, and let the next start decide.
      if (plan.remoteCampaignChanged) return park(MSG.otherDevice);
      // Only this device's changes are in play: they go up again on top of the server's revision.
      // A preference the server changed is left for the next start (its base is not moved on).
      s.revision = result.doc.revision;
      s.dirty = plan.dirty;
      for (const key of CLOUD_KEYS) {
        const agreed = plan.agreed[key];
        if (plan.take[key] === undefined && agreed !== undefined) s.base[key] = agreed;
      }
      persist();
      if (mayRetry) return pushOnce(keepalive, false);
      failures += 1;
    } else if (result.kind === 'refused') park(refusedMessage(result.status));
    else failures += 1;
  }

  function push(keepalive: boolean): Promise<void> {
    if (inFlight !== null) {
      again = true;
      return inFlight;
    }
    const run = pushOnce(keepalive, true)
      .catch(() => {
        failures += 1;
      })
      .then(() => {
        inFlight = null;
        const s = state;
        if (on && parked === null && s !== null && s.dirty.length > 0) {
          const retry = Math.min(RETRY_MAX_MS, RETRY_MIN_MS * 2 ** Math.max(0, failures - 1));
          schedule(failures > 0 ? retry : again ? 0 : PUSH_DELAY_MS);
        }
        again = false;
        changed();
      });
    inFlight = run;
    changed();
    return run;
  }

  async function whenIdle(): Promise<void> {
    while (inFlight !== null) await inFlight;
  }

  async function pullAtBoot(): Promise<CloudStatus> {
    if (storage === null || !(await probe())) return status();
    const stored = loadState(storage);
    const s = stored ?? freshState(newCode(deps.randomBytes));
    state = s;
    // A new device, or a new player: the code exists from now on, but nothing is uploaded until
    // there is a real save to upload - a visitor who never plays leaves no file on the server.
    if (stored === null) note = MSG.newCode;
    else {
      const got = await transport.get(s.code, BOOT_PULL_TIMEOUT_MS);
      if (got.kind === 'ok') {
        const plan = mergeWith(s, got.doc);
        applyMerge(storage, local, s, got.doc, plan, now());
        if (plan.conflict !== null) conflictNote = plan.conflict.winner === 'local' ? MSG.localWon : MSG.remoteWon;
        else if (Object.keys(plan.take).length > 0) note = MSG.pulled;
      } else if (got.kind === 'none') {
        // Never uploaded - or the server has lost what it had (a new volume) and this device's copy goes up again.
        if (s.revision > 0) s.dirty = CLOUD_KEYS.filter((k) => (local.read(k) ?? '') !== '');
        if (s.dirty.length > 0 && s.dirtyAt === null) s.dirtyAt = now();
        note = s.dirty.length > 0 ? MSG.serverEmpty : MSG.newCode;
        s.revision = 0;
        s.base = {};
      } else if (got.kind === 'refused') park(refusedMessage(got.status));
      else failures = 1;
    }
    persist();
    remember();
    if (parked === null && s.dirty.length > 0) schedule(failures > 0 ? RETRY_MIN_MS : PUSH_DELAY_MS);
    changed();
    return status();
  }

  function probe(): Promise<boolean> {
    probing ??= (async (): Promise<boolean> => {
      if (storage === null || !/^https?:\/\//.test(deps.location.origin)) return false;
      on = await transport.probe();
      return on;
    })();
    return probing;
  }

  return {
    probe,
    pullAtBoot,
    enabled: () => on,

    touch(key) {
      const s = state;
      if (!on || s === null || replaced || !isCloudKey(key)) return;
      const value = local.read(key) ?? '';
      if (known.get(key) === value) return;
      known.set(key, value);
      if (!s.dirty.includes(key)) s.dirty.push(key);
      s.dirtyAt = now();
      persist();
      if (parked === null) schedule(key === CLOUD_KEY.suspend ? SUSPEND_PUSH_DELAY_MS : PUSH_DELAY_MS);
    },

    flush() {
      if (!on || state === null || parked !== null || state.dirty.length === 0) return;
      cancelTimer();
      void push(true);
    },

    whenIdle,

    dispose() {
      cancelTimer();
      listeners.clear();
    },

    status,
    code: () => (on ? (state?.code ?? null) : null),

    async useCode(text) {
      const s = state;
      if (!on || storage === null || s === null) return MSG.unavailable;
      cancelTimer();
      await whenIdle();
      const outcome = await adoptCode({ storage, transport, state: s, text, now, local });
      if (outcome.state === null) {
        persist();
        if (parked === null && s.dirty.length > 0) schedule(PUSH_DELAY_MS);
        return outcome.message;
      }
      state = outcome.state;
      // The running game still holds the old save in memory: nothing it writes from here on may
      // be sent under the new code. `restart()` is the only way forward.
      replaced = true;
      park(MSG.replaced);
      persist();
      changed();
      return null;
    },

    restart: () => deps.location.reload(),

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
